import { useDarkMode } from "../../../context/ThemeContext";
import { getThemeClasses } from "../../../utils/ThemeClasses";
import { useForm, Controller } from "react-hook-form";
import { useCallback, useEffect, useMemo, useState } from "react";
import { useQuery } from "@tanstack/react-query";
import { useAlert } from "../../../context/AlertContext";
import InfiniteDeviceSelect, { useReportDeviceIds } from "../../../components/InfiniteDeviceSelect";
import { allDevicesQueryOptions } from "../../../features/devices/queries";
import { useReportCompanyScope } from "../hooks/useReportCompanyScope";
import { useReportGpsQualityStore } from "../../../store/reports/reportGpsQualityStore";
import ReportPointsTable, { type ReportTableColumn } from "../components/ReportPointsTable";
import { ReportStatCard } from "../components/ReportCharts";
import ReportLoadingPanel from "../components/ReportLoadingPanel";
import { toLocalInputValue, makeDateTimeFormatter, resolveTz } from "../utils/reportDateFormat";
import { exportHistoricToExcel, exportHistoricToPdf } from "../utils/exportHistoric";
import { diagnose } from "../utils/gpsQuality";
import { fmtDuration } from "../../quick-reports/quickReports.compute";
import ReportActionButton from "../components/ReportActionButton";
import ReportDateRangePicker from "../components/ReportDateRangePicker";

// Se lee el historial en el navegador (como Temperatura y Voltaje).
const MAX_RANGE_DAYS = 31;
const DAY_MS = 24 * 60 * 60 * 1000;

interface UnitRow {
  id: string;
  unidad: string;
  reportes: string;
  intervalo: string;
  huecosMov: string;
  huecos: string;
  saltos: string;
  satelites: string;
  senal: string;
  diagnostico: string;
}

interface IssueRow {
  id: string;
  unidad: string;
  tipo: string;
  inicio: string;
  detalle: string;
  ubicacion: string;
}

const UNIT_COLUMNS: ReportTableColumn<UnitRow>[] = [
  { key: "unidad", header: "Unidad" },
  // Lo primero que busca soporte; al final quedaba fuera de vista con scroll.
  { key: "diagnostico", header: "Diagnóstico" },
  { key: "reportes", header: "Reportes", align: "right" },
  { key: "intervalo", header: "Intervalo típico", align: "right" },
  { key: "huecosMov", header: "Huecos en movimiento", align: "right" },
  { key: "huecos", header: "Huecos > 30 min", align: "right" },
  { key: "saltos", header: "Saltos", align: "right" },
  { key: "satelites", header: "Satélites prom.", align: "right" },
  { key: "senal", header: "Señal celular", align: "right" },
];

const ISSUE_COLUMNS: ReportTableColumn<IssueRow>[] = [
  { key: "unidad", header: "Unidad" },
  { key: "tipo", header: "Tipo" },
  { key: "inicio", header: "Inicio" },
  { key: "detalle", header: "Detalle" },
  { key: "ubicacion", header: "Ubicación" },
];

const KIND_LABEL = { gap_moving: "Hueco en movimiento", gap: "Hueco (detenido)", jump: "Salto de posición" } as const;

export default function GpsQualitySection() {
  const { isDarkMode } = useDarkMode();
  const themeClasses = getThemeClasses(isDarkMode);
  const { loadedDeviceIds, loadedCount, totalDevices } = useReportDeviceIds();
  const { showAlert } = useAlert();

  const loadRange = useReportGpsQualityStore((s) => s.loadRange);
  const clear = useReportGpsQualityStore((s) => s.clear);
  const resultsState = useReportGpsQualityStore((s) => s.results);
  const order = useReportGpsQualityStore((s) => s.order);
  const loading = useReportGpsQualityStore((s) => s.loading);
  const tz = useReportGpsQualityStore((s) => s.tz);

  const [exporting, setExporting] = useState<"pdf" | "excel" | null>(null);
  const [onlyProblems, setOnlyProblems] = useState(false);

  const { control, setValue, watch, getValues, register, formState: { errors } } = useForm<{
    devices: string[];
    startDate: string;
    endDate: string;
  }>({ mode: "onBlur", defaultValues: { devices: [], startDate: "", endDate: "" } });

  const { companyId, disableWithoutCompany } = useReportCompanyScope(setValue, () => clear());
  const { data: allDevices } = useQuery({ ...allDevicesQueryOptions(companyId) });

  const selectedDevices = watch("devices");
  const startDate = watch("startDate");
  const endDate = watch("endDate");
  const rangeDays = startDate && endDate ? (new Date(endDate).getTime() - new Date(startDate).getTime()) / DAY_MS : -1;
  const ready = (selectedDevices?.length ?? 0) > 0 && rangeDays >= 0 && rangeDays <= MAX_RANGE_DAYS;

  const nameById = useCallback((): Record<string, string> => {
    const map: Record<string, string> = {};
    (allDevices ?? []).forEach((d: any) => {
      map[d.id] = d.vehicle?.name || d.identifier || d.id;
    });
    return map;
  }, [allDevices]);

  const onView = async () => {
    const { devices, startDate: from, endDate: to } = getValues();
    const names = nameById();
    await loadRange(
      devices.map((id) => ({ deviceId: id, vehicleName: names[id] || id })),
      new Date(from).toISOString(),
      new Date(to).toISOString(),
    );
  };

  const results = useMemo(() => order.map((id) => resultsState[id]).filter(Boolean), [order, resultsState]);
  const fmtDateTime = useMemo(() => makeDateTimeFormatter(resolveTz(tz), false), [tz]);

  const unitRows = useMemo<UnitRow[]>(
    () =>
      results
        .filter((r) => r.summary)
        .map((r) => {
          const s = r.summary!;
          return {
            id: r.deviceId,
            unidad: r.vehicleName,
            reportes: String(s.points),
            intervalo: s.medianIntervalSec == null ? "—" : s.medianIntervalSec < 90 ? `${Math.round(s.medianIntervalSec)} s` : fmtDuration(s.medianIntervalSec),
            huecosMov: String(s.gapsMoving),
            huecos: String(s.gaps),
            saltos: String(s.jumps),
            satelites: s.satsAvg == null ? "—" : s.satsAvg.toFixed(0),
            senal: s.gsmAvg == null ? "—" : `${s.gsmAvg.toFixed(1)} / 5`,
            diagnostico: r.error ? `${diagnose(s)} (${r.error})` : diagnose(s),
          };
        })
        .filter((row) => !onlyProblems || row.diagnostico !== "Sin problemas"),
    [results, onlyProblems],
  );

  const issueRows = useMemo<IssueRow[]>(
    () =>
      results.flatMap((r) =>
        (r.summary?.issues ?? [])
          .filter((i) => i.kind !== "gap")
          .map((i, n) => ({
            id: `${r.deviceId}-${i.start}-${n}`,
            unidad: r.vehicleName,
            tipo: KIND_LABEL[i.kind],
            inicio: fmtDateTime(i.start),
            detalle:
              i.kind === "jump"
                ? `${i.km.toFixed(1)} km en ${fmtDuration((i.end - i.start) / 1000)} (≈ ${Math.round(i.kmh!)} km/h)`
                : `Sin reportar ${fmtDuration((i.end - i.start) / 1000)}; reapareció a ${i.km.toFixed(1)} km`,
            ubicacion: `${i.lat.toFixed(5)}, ${i.lng.toFixed(5)}`,
          })),
      ),
    [results, fmtDateTime],
  );

  const totals = useMemo(() => {
    const done = results.filter((r) => r.summary);
    return {
      units: done.length,
      withProblems: done.filter((r) => diagnose(r.summary!) !== "Sin problemas" && r.summary!.points > 0).length,
      gapsMoving: done.reduce((n, r) => n + r.summary!.gapsMoving, 0),
      jumps: done.reduce((n, r) => n + r.summary!.jumps, 0),
      silent: done.filter((r) => r.summary!.points === 0).length,
    };
  }, [results]);

  const onDownload = async (type: "pdf" | "excel") => {
    const { startDate: from, endDate: to } = getValues();
    const fileName = `calidad_gps_${from.split("T")[0]}_${to.split("T")[0]}.${type === "pdf" ? "pdf" : "xlsx"}`;
    const period =
      `Período: ${from.replace("T", " ")} al ${to.replace("T", " ")}` +
      ` | Con problemas: ${totals.withProblems} de ${totals.units} | Huecos en movimiento: ${totals.gapsMoving} | Saltos: ${totals.jumps}`;
    setExporting(type);
    await new Promise((r) => setTimeout(r, 50));
    try {
      const meta = { title: "Informe de Calidad GPS", period, fileName };
      if (type === "excel") await exportHistoricToExcel(unitRows, UNIT_COLUMNS, meta);
      else exportHistoricToPdf(unitRows, UNIT_COLUMNS, meta);
    } catch {
      showAlert({
        title: "Error al descargar",
        text: "No se pudo generar el archivo de calidad GPS.",
        icon: "error",
        confirmButtonText: "OK",
        confirmButtonColor: "#2563eb",
        showCancelButton: false,
      });
    } finally {
      setExporting(null);
    }
  };

  useEffect(() => {
    const now = new Date();
    setValue("startDate", toLocalInputValue(new Date(now.getTime() - 7 * DAY_MS)));
    setValue("endDate", toLocalInputValue(now));
  }, [setValue]);

  useEffect(() => () => clear(), [clear]);

  const hasResults = totals.units > 0 && !loading;

  return (
    <div className="h-full w-full overflow-y-auto p-2 sm:p-4">
      <div className={`flex flex-col lg:flex-row ${themeClasses.cardBorder} rounded-lg ${themeClasses.cardBg} p-3 sm:p-3.5 gap-3 justify-between lg:items-end`}>
        <div className="flex flex-col sm:flex-row sm:flex-wrap items-stretch sm:items-center gap-3 sm:gap-4 w-full lg:w-auto">
          <div className="flex flex-col gap-1 min-w-52 w-full lg:w-auto">
            <div className="flex justify-between items-center">
              <label htmlFor="devices" className={`${themeClasses.text} text-xs`}>
                Equipos {selectedDevices?.length > 0 && `(${selectedDevices.length}/${totalDevices})`}
              </label>
              <button
                type="button"
                onClick={() => setValue("devices", selectedDevices?.length === loadedDeviceIds.length ? [] : loadedDeviceIds)}
                disabled={disableWithoutCompany}
                className={`text-xs px-2 py-1 rounded ${themeClasses.buttonBg} text-white ${themeClasses.buttonHover2} hover:opacity-90 disabled:opacity-50`}
              >
                {selectedDevices?.length === loadedDeviceIds.length && loadedCount > 0 ? "Deseleccionar todos" : `Seleccionar todos (${loadedCount})`}
              </button>
            </div>
            <Controller
              name="devices"
              control={control}
              render={({ field }) => (
                <InfiniteDeviceSelect value={field.value} onChange={field.onChange} isDarkMode={isDarkMode} themeClasses={themeClasses} />
              )}
            />
          </div>
          <div className="flex flex-col gap-1 w-full lg:w-auto">
            <label className={`${themeClasses.text} text-xs`}>Rango de fechas</label>
            <input type="hidden" {...register("startDate", { required: "Fecha inicio requerida" })} />
            <input type="hidden" {...register("endDate", { required: "Fecha fin requerida" })} />
            <ReportDateRangePicker
              startValue={startDate}
              endValue={endDate}
              onChangeStart={(v) => setValue("startDate", v, { shouldValidate: true, shouldDirty: true })}
              onChangeEnd={(v) => setValue("endDate", v, { shouldValidate: true, shouldDirty: true })}
              startError={errors.startDate?.message}
              endError={errors.endDate?.message}
            />
          </div>
        </div>
        <ReportActionButton
          type="button"
          onClick={onView}
          disabled={loading || !ready || disableWithoutCompany}
          title={rangeDays > MAX_RANGE_DAYS ? `Admite hasta ${MAX_RANGE_DAYS} días` : undefined}
        >
          Revisar equipos
        </ReportActionButton>
      </div>

      {loading && (
        <ReportLoadingPanel
          isDarkMode={isDarkMode}
          icon="ri-signal-tower-line"
          title="Revisando los reportes…"
          subtitle="Buscando huecos de reporte y posiciones que brincan"
        />
      )}

      {hasResults && (
        <div className={`mt-4 flex flex-col gap-4 ${themeClasses.cardBorder} rounded-lg ${themeClasses.cardBg} p-3 sm:p-4`}>
          <div className="flex flex-wrap items-center justify-between gap-3">
            <label className={`flex items-center gap-2 text-sm ${themeClasses.text}`}>
              <input type="checkbox" checked={onlyProblems} onChange={(e) => setOnlyProblems(e.target.checked)} />
              Sólo unidades con problemas
            </label>
            <div className="flex flex-wrap items-center gap-2">
              {(["pdf", "excel"] as const).map((type) => (
                <button
                  key={type}
                  type="button"
                  onClick={() => onDownload(type)}
                  disabled={exporting !== null || unitRows.length === 0}
                  className={`text-xs px-3 py-1 rounded ${themeClasses.buttonBg} text-white ${themeClasses.buttonHover2} disabled:opacity-50`}
                >
                  {exporting === type ? "Generando..." : `Descargar ${type === "pdf" ? "PDF" : "Excel"}`}
                </button>
              ))}
              <button
                type="button"
                onClick={clear}
                className={`text-xs px-3 py-1 rounded ${themeClasses.buttonBg} text-white ${themeClasses.buttonHover2}`}
              >
                Cerrar
              </button>
            </div>
          </div>

          <div className="grid gap-3 grid-cols-2 lg:grid-cols-4">
            <ReportStatCard icon="ri-error-warning-line" label="Unidades con problemas" value={`${totals.withProblems} de ${totals.units}`} isDarkMode={isDarkMode} />
            <ReportStatCard icon="ri-signal-wifi-off-line" label="Huecos en movimiento" value={String(totals.gapsMoving)} isDarkMode={isDarkMode} />
            <ReportStatCard icon="ri-map-pin-range-line" label="Saltos de posición" value={String(totals.jumps)} isDarkMode={isDarkMode} />
            <ReportStatCard icon="ri-time-line" label="Sin reportes en el rango" value={String(totals.silent)} isDarkMode={isDarkMode} />
          </div>

          <ReportPointsTable data={unitRows} columns={UNIT_COLUMNS} isDarkMode={isDarkMode} emptyText="Ninguna unidad con problemas en el rango." />

          {issueRows.length > 0 && (
            <>
              <p className={`text-sm font-semibold ${themeClasses.text}`}>Detalle: huecos en movimiento y saltos</p>
              <ReportPointsTable data={issueRows} columns={ISSUE_COLUMNS} isDarkMode={isDarkMode} emptyText="" />
            </>
          )}
        </div>
      )}

      {!hasResults && !loading && (
        <div className={`mt-4 ${themeClasses.cardBorder} rounded-lg ${themeClasses.cardBg} p-3 sm:p-4`}>
          <div className={`flex flex-col items-center justify-center gap-3 px-6 py-10 text-center rounded-xl border border-dashed ${themeClasses.cardBorder} ${isDarkMode ? "bg-white/5" : "bg-black/[0.02]"}`}>
            <span className={`inline-flex items-center justify-center w-14 h-14 rounded-full ${isDarkMode ? "bg-primary-500/15" : "bg-primary-500/10"}`}>
              <i className="ri-signal-tower-line text-primary-500 text-2xl" />
            </span>
            <p className={`text-sm font-semibold ${themeClasses.text}`}>Calidad GPS</p>
            <p className={`text-xs max-w-md ${themeClasses.textSecondary}`}>
              Revisa si los equipos dejan de reportar en movimiento o mandan posiciones que brincan (hasta {MAX_RANGE_DAYS} días). Sirve para
              diagnosticar «la unidad no aparece» o «la unidad brinca» antes de mandar a revisar el equipo.
            </p>
          </div>
        </div>
      )}
    </div>
  );
}
